import { useState } from 'react';
import { Box, Button, Icon, Tag } from '@neshane/blueberry';
import { TermsFormModal, TermsPageLoader } from '../components';
import { useTermsAdmin } from '../hooks';
import { useToast } from '../../../common/hooks';
import './TermsPage.css';

/**
 * Admin terms & conditions management page.
 * Lists versions, allows creating/editing content and activating a version.
 */
export const TermsPage = () => {
    const {
        versions,
        loading,
        actionLoading,
        refresh,
        createVersion,
        updateVersion,
        activateVersion,
    } = useTermsAdmin();
    const { success, error: showError } = useToast();
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    if (loading && versions.length === 0) {
        return <TermsPageLoader />;
    }

    const handleCreateSubmit = async (values) => {
        const result = await createVersion({ title: values.title.trim(), content: values.content.trim(), isActive: true });
        if (result.success) {
            success('قوانین جدید ایجاد شد');
            setIsFormModalOpen(false);
            return;
        }
        showError(result.error);
    };

    const handleEditSubmit = async (values) => {
        const result = await updateVersion(editing.id, { title: values.title.trim(), content: values.content.trim() });
        if (result.success) {
            success('قوانین بروزرسانی شد');
            setEditing(null);
            setIsFormModalOpen(false);
            return;
        }
        showError(result.error);
    };

    const handleActivate = async (version) => {
        const result = await activateVersion(version.id);
        if (result.success) {
            success(`نسخه ${version.version} فعال شد`);
            return;
        }
        showError(result.error);
    };

    return (
        <div className="TermsPage">
            <header className="TermsPage__header">
                <div className="TermsPage__header__titles">
                    <h1 className="heading-l text-primary">قوانین و مقررات</h1>
                    <p className="TermsPage__header__description">
                        مدیریت نسخه‌های قوانین و مقررات؛ کاربران باید نسخه فعال را مطالعه و تایید کنند.
                    </p>
                </div>
                <div className="TermsPage__header__actions">
                    <Button
                        text="ایجاد نسخه جدید"
                        iconName="plus"
                        variant="primaryBrand"
                        onClick={() => { setEditing(null); setIsFormModalOpen(true); }}
                    />
                    <Button
                        text="بروزرسانی"
                        iconName="arrow-path"
                        variant="secondaryNeutral"
                        onClick={refresh}
                    />
                </div>
            </header>

            <Box className="flex flex-col gap-16">
                {versions.length === 0 ? (
                    <div className="TermsPage__empty">
                        <Icon name="document" size="l" />
                        <p className="body-m text-secondary-bg-primary">هنوز نسخه‌ای از قوانین ایجاد نشده است.</p>
                        <Button
                            text="ایجاد نسخه جدید"
                            iconName="plus"
                            variant="primaryBrand"
                            onClick={() => { setEditing(null); setIsFormModalOpen(true); }}
                        />
                    </div>
                ) : (
                    <ul className="TermsPage__list">
                        {versions.map((version) => (
                            <li key={version.id} className="TermsPage__row">
                                <div className="TermsPage__row__info">
                                    <div className="flex items-center gap-8">
                                        <span className="title-m text-primary">{version.getVersionLabel()}</span>
                                        {version.isActive ? (
                                            <Tag variant="success">فعال</Tag>
                                        ) : (
                                            <Tag variant="neutral">غیرفعال</Tag>
                                        )}
                                    </div>
                                    <p className="body-m text-primary">{version.title}</p>
                                    <p className="body-s text-secondary-bg-primary">
                                        آخرین بروزرسانی: {version.getFormattedUpdatedAt()}
                                    </p>
                                </div>
                                <div className="TermsPage__row__actions">
                                    <Button
                                        variant="tertiaryNeutral"
                                        text="مشاهده"
                                        onClick={() => { setEditing(version); setIsFormModalOpen(true); }}
                                    />
                                    {!version.isActive && (
                                        <Button
                                            variant="secondaryBrand"
                                            text="فعال‌سازی"
                                            isLoading={actionLoading}
                                            onClick={() => handleActivate(version)}
                                        />
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Box>

            {isFormModalOpen && (
                <TermsFormModal
                    version={editing}
                    isSubmitting={actionLoading}
                    onSubmit={editing ? handleEditSubmit : handleCreateSubmit}
                    onCancel={() => setIsFormModalOpen(false)}
                />
            )}
        </div>
    );
};

export default TermsPage;